import React from 'react';

import { CompactMetric, DisclosurePanel } from '../components/AppShellPrimitives';
import { EvidencePostureCard, EvidencePostureChip, formatEvidenceDateTime } from '../evidencePosture';
import { sourceTypeLabel } from '../appHelpers';

function pluralize(count, singular, plural = `${singular}s`) {
  return count === 1 ? singular : plural;
}

function formatDateTime(value) {
  if (!value) {
    return 'Not available yet';
  }
  return new Date(value).toLocaleString();
}

function formatCount(value) {
  return typeof value === 'number' ? value.toLocaleString() : 'Not available';
}

function formatConfidence(value) {
  return typeof value === 'number' ? `${Math.round(value * 100)}%` : 'Not available';
}

function formatAnalysisTimeWindow(analysis) {
  if (!analysis?.time_range_start && !analysis?.time_range_end) {
    return 'Not available';
  }
  if (analysis.time_range_start && analysis.time_range_end) {
    return `${formatEvidenceDateTime(analysis.time_range_start)} to ${formatEvidenceDateTime(analysis.time_range_end)}`;
  }
  return formatEvidenceDateTime(analysis.time_range_start || analysis.time_range_end);
}

function mappingProfileStatusLabel(status) {
  switch (status) {
    case 'stable':
      return 'Profile stable';
    case 'drift_detected':
      return 'Profile drift';
    case 'new_profile':
      return 'New profile';
    default:
      return null;
  }
}

function componentGraphContextLabel(context) {
  switch (context?.mode) {
    case 'existing_component_graph':
      return 'Mapped using existing component graph';
    case 'clean_new_workflow':
      return 'Mapped from a clean/new workflow';
    default:
      return null;
  }
}

function TraceAnalysisSourceSummary({ analysis }) {
  if (!analysis) {
    return null;
  }
  const warnings = Array.from(new Set([...(analysis.warnings || []), ...(analysis.mapping_profile_drift_warnings || [])]));
  const unknownFields = analysis.unknown_fields || [];
  const detectedConventions = analysis.detected_conventions || [];
  const confidentlyMapped = analysis.confidently_mapped || [];
  const profileStatusLabel = mappingProfileStatusLabel(analysis.mapping_profile_drift_status);
  const profileStatusClass = analysis.mapping_profile_drift_status === 'drift_detected' ? 'chip chip-warning' : 'chip chip-success';
  const graphContextLabel = componentGraphContextLabel(analysis.component_graph_context);
  const mappingWarning = analysis.mapping_context_warning?.message;
  const duplicateWarning = analysis.duplicate_content_warning?.message;
  return (
    <section className="trace-analysis-summary runtime-source-analysis" aria-label="Latest trace analysis output">
      <div className="trace-analysis-summary-header">
        <div>
          <p className="detail-kicker">Latest trace analysis</p>
          <h4>{analysis.detected_format || 'Detected trace evidence'}</h4>
        </div>
        <span className="confidence-badge">{formatConfidence(analysis.mapping_confidence)}</span>
      </div>
      <div className="trace-analysis-metrics">
        <div>
          <span>Source shape</span>
          <strong>{analysis.raw_layout || 'Not available'}</strong>
        </div>
        <div>
          <span>Records</span>
          <strong>{formatCount(analysis.record_count)}</strong>
        </div>
        <div>
          <span>Traces</span>
          <strong>{formatCount(analysis.trace_count)}</strong>
        </div>
        <div>
          <span>Spans</span>
          <strong>{formatCount(analysis.span_count)}</strong>
        </div>
        <div>
          <span>Model calls</span>
          <strong>{formatCount(analysis.model_call_candidates)}</strong>
        </div>
        <div>
          <span>Tool calls</span>
          <strong>{formatCount(analysis.tool_call_candidates)}</strong>
        </div>
      </div>
      <div className="trace-analysis-window">
        <span>Time window</span>
        <strong>{formatAnalysisTimeWindow(analysis)}</strong>
      </div>
      <div className="trace-analysis-chip-row" aria-label="Trace mapping details">
        {analysis.semantic_profile ? <span className="chip">{analysis.semantic_profile}</span> : null}
        {analysis.requested_source_type === 'smart_trace' ? <span className="chip chip-success">Smart trace upload</span> : null}
        {analysis.mapping_profile_id ? <span className="chip chip-success">Mapping profile saved</span> : null}
        {analysis.mapping_fingerprint?.fingerprint ? <span className="chip chip-success">Mapping fingerprint saved</span> : null}
        {graphContextLabel ? <span className="chip chip-success">{graphContextLabel}</span> : null}
        {profileStatusLabel ? <span className={profileStatusClass}>{profileStatusLabel}</span> : null}
        {detectedConventions.map((item) => (
          <span className="chip chip-success" key={`convention-${item}`}>{item}</span>
        ))}
        {confidentlyMapped.slice(0, 3).map((item) => (
          <span className="chip chip-success" key={`mapped-${item}`}>{item}</span>
        ))}
        {typeof analysis.unknown_field_count === 'number' ? (
          <span className={analysis.unknown_field_count > 0 ? 'chip chip-warning' : 'chip chip-success'}>
            {analysis.unknown_field_count} unknown fields
          </span>
        ) : null}
      </div>
      {warnings.length ? (
        <div className="runtime-upload-warning-list" aria-label="Trace analysis warnings">
          {warnings.slice(0, 3).map((warning) => (
            <span className="chip chip-warning" key={warning}>{warning}</span>
          ))}
        </div>
      ) : null}
      {mappingWarning || duplicateWarning ? (
        <div className="runtime-upload-warning-list" aria-label="Trace mapping context warnings">
          {mappingWarning ? <span className="chip chip-warning">{mappingWarning}</span> : null}
          {duplicateWarning ? <span className="chip chip-warning">{duplicateWarning}</span> : null}
        </div>
      ) : null}
      {unknownFields.length ? (
        <DisclosurePanel title="Unknown fields" summaryMeta={`${unknownFields.length} shown`}>
          <div className="trace-analysis-field-list">
            {unknownFields.map((field) => (
              <span className="chip" key={field}>{field}</span>
            ))}
          </div>
        </DisclosurePanel>
      ) : null}
    </section>
  );
}

function trendDirectionLabel(direction) {
  switch (direction) {
    case 'improving':
      return 'Improving';
    case 'regressing':
    case 'worsening':
      return 'Regressing';
    case 'mixed':
      return 'Mixed trend';
    case 'stable':
      return 'Stable trend';
    case 'unavailable':
    default:
      return 'Trend unavailable';
  }
}

function formatCostNumber(value) {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return 'n/a';
  }
  return Number.isInteger(value) ? `${value}` : value.toFixed(2).replace(/\.?0+$/, '');
}

function formatCostUnit(unit) {
  if (!unit) {
    return 'workflow units';
  }
  const labels = {
    tokens_per_run: 'tokens/run',
    extra_steps_per_run: 'extra steps/run',
    extra_loop_steps_per_run: 'extra loop steps/run',
    screening_calls_per_kept_doc: 'screening calls/kept document',
    seconds_per_run: 'seconds/run',
    at_risk_io_steps_per_100: 'retrieval or external steps/100',
  };
  return labels[unit] || unit.replaceAll('_', ' ');
}

function formatObservedClaimValue(claim) {
  const range = claim?.observed_value_range;
  if (!range || typeof range.lower_bound !== 'number') {
    return '';
  }
  const lower = formatCostNumber(range.lower_bound);
  const upper = typeof range.upper_bound === 'number' ? formatCostNumber(range.upper_bound) : lower;
  const value = lower === upper ? lower : `${lower} to ${upper}`;
  return `${value} ${formatCostUnit(claim.observed_unit)}`;
}

function overviewSafeCostText(value) {
  const text = value || '';
  return /(\$|usd|eur|savings|roi|payback|business value)/i.test(text)
    ? 'Observed workflow-unit impact is present in the current evidence window.'
    : text;
}

function SummaryStat({ label, value, tone = 'default', compact = false }) {
  const className = [
    'detail-card',
    'summary-card',
    tone === 'muted' ? 'summary-card-muted' : '',
    compact ? 'summary-card-compact' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={className}>
      <span className="summary-card-label">{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function SectionHeader({ title, action }) {
  return (
    <div className="chapter-header">
      <div>
        <h3>{title}</h3>
      </div>
      {action}
    </div>
  );
}

function actionLooksValidationRelated(action) {
  if (!action) {
    return false;
  }
  return /validation|guardrail/i.test(`${action.title || ''} ${action.summary || ''}`);
}

export default function OverviewView({
  selectedSystem,
  overview,
  banner,
  findingsHref,
  runtimeSourceActionStatus,
  canOperateRuntimeSources,
  runtimeSourceActionLoadingRef,
  onReplayRuntimeSource,
  onBackfillRuntimeSource,
  runtimeSourceWindowLabel,
}) {
  const reliabilitySummary = overview.reliability_summary || {
    workflows_requiring_validation_count: 0,
    low_confidence_workflow_count: 0,
  };
  const atRiskWorkflowCount = reliabilitySummary.verdict_counts?.operationally_unstable || 0;
  const analysisWindowSummary = `${overview.analysis_window.analysis_run_count} ${pluralize(
    overview.analysis_window.analysis_run_count,
    'analysis',
    'analyses'
  )} · ${overview.analysis_window.run_count} ${pluralize(overview.analysis_window.run_count, 'run')}`;
  const validationGapSummary = `${reliabilitySummary.workflows_requiring_validation_count} stable ${pluralize(
    reliabilitySummary.workflows_requiring_validation_count,
    'workflow'
  )} pending validation`;
  const lowConfidenceSummary = `${reliabilitySummary.low_confidence_workflow_count} ${pluralize(
    reliabilitySummary.low_confidence_workflow_count,
    'workflow'
  )} ${pluralize(reliabilitySummary.low_confidence_workflow_count, 'is', 'are')} low confidence`;
  const improvementPlanSummary = overview.improvement_plan_summary || {
    active_count: 0,
    awaiting_evidence_count: 0,
    review_ready_count: 0,
    monitoring_count: 0,
  };
  const costClaimSummary = overview.cost_claim_summary;
  const primaryCostClaim = costClaimSummary?.recovery_claims?.[0] || costClaimSummary?.top_claims?.[0];
  const primaryCostValue = formatObservedClaimValue(primaryCostClaim);
  const topFinding = overview.top_findings?.[0] || null;
  const topAction = overview.recommended_next_actions?.[0] || null;
  const topActionIsValidation = actionLooksValidationRelated(topAction);
  let nextActionHref = `#/systems/${selectedSystem.id}/findings`;
  if (topAction) {
    nextActionHref =
      topActionIsValidation || !topAction.recommendation_id
        ? `#/systems/${selectedSystem.id}/findings/${topAction.finding_id}`
        : `#/systems/${selectedSystem.id}/recommendations/${topAction.recommendation_id}`;
  } else if (topFinding) {
    nextActionHref = `#/systems/${selectedSystem.id}/findings/${topFinding.finding_id}`;
  }
  const nextActionLabel = topAction
    ? topActionIsValidation
      ? 'Open validation next step'
      : 'Open recommended action'
    : topFinding
      ? 'Open top finding'
      : 'Open findings';

  return (
    <section className="content-grid single-column">
      <div className="panel detail-panel">
        {banner}
        <div className="panel-header">
          <div>
            <p className="detail-kicker">Runtime reliability</p>
            <h2>Overview</h2>
          </div>
        </div>

        {overview.analysis_window.status === 'no_completed_analysis' ? (
          <div className="empty-state-card">
            <strong>No completed analysis yet</strong>
            <p>Overview cards will populate as soon as the first workflow analysis finishes for this system.</p>
          </div>
        ) : (
          <>
            <section className="calm-overview-priority" aria-label="What needs attention next">
              <article className="detail-card calm-primary-card">
                <p className="detail-kicker">What needs attention next</p>
                <h3>{reliabilitySummary.summary_label || 'Runtime reliability is still being established.'}</h3>
                <div className="calm-overview-grid">
                  <CompactMetric label="At-risk workflows" value={atRiskWorkflowCount} />
                  <CompactMetric label="Active findings" value={overview.posture.active_finding_count} />
                  <div className="compact-metric compact-metric-muted">
                    <span>Freshness</span>
                    <EvidencePostureChip posture={overview.evidence_posture} />
                  </div>
                </div>
              </article>

              <article className="detail-card calm-action-card">
                <p className="detail-kicker">Start here</p>
                <h3>{topAction?.title || topFinding?.title || 'Review findings'}</h3>
                <p>{topAction?.summary || topFinding?.summary || 'Open the findings queue to review the highest-priority runtime issue.'}</p>
                <a className="primary-button action-link-button" href={nextActionHref}>
                  {nextActionLabel}
                </a>
              </article>
            </section>

            <section className="overview-band">
              <SectionHeader title="Current runtime" />
              <div className="overview-current-grid">
                <section className="detail-card overview-card overview-card-primary overview-posture-card">
                  <div className="chapter-inline-header">
                    <strong>Runtime summary</strong>
                    <EvidencePostureChip posture={overview.evidence_posture} prefix="Freshness" />
                  </div>
                  <div className="overview-reliability-copy overview-posture-copy">
                    <p>{reliabilitySummary.summary_label || 'Workflow-level reliability verdicts will appear here as evidence accumulates.'}</p>
                  </div>
                  <div className="summary-stat-row overview-posture-stats">
                    <SummaryStat compact label="At-risk workflows" value={atRiskWorkflowCount} />
                    <SummaryStat compact label="Active findings" value={overview.posture.active_finding_count} />
                    <SummaryStat
                      compact
                      label="Low confidence"
                      value={reliabilitySummary.low_confidence_workflow_count}
                      tone="muted"
                    />
                  </div>
                  <div className="overview-reliability-buckets">
                    <span className="summary-card-label">Improvement plans</span>
                    <div className="chip-row">
                      <span className="chip chip-emphasis">Active {improvementPlanSummary.active_count}</span>
                      <span className="chip">Awaiting evidence {improvementPlanSummary.awaiting_evidence_count}</span>
                      <span className="chip">Review ready {improvementPlanSummary.review_ready_count}</span>
                      <span className="chip">Monitoring {improvementPlanSummary.monitoring_count}</span>
                    </div>
                  </div>
                </section>

              </div>
            </section>

            <section className="overview-band">
              <SectionHeader title="What is driving the runtime summary" />
              <div className="overview-drivers-grid">
                <section className="detail-card overview-card">
                  <div className="overview-section-header">
                    <strong>Top active findings</strong>
                    <a className="pill-link" href={`#/systems/${selectedSystem.id}/findings`}>
                      See all findings
                    </a>
                  </div>
                  {overview.top_findings.length ? (
                    <div className="stack compact-stack">
                      {overview.top_findings.map((finding) => (
                        <a
                          className="overview-row-link overview-row-link-emphasis"
                          href={`#/systems/${selectedSystem.id}/findings/${finding.finding_id}`}
                          key={finding.finding_id}
                        >
                          <div>
                            <strong>{finding.title}</strong>
                            <p>{finding.summary}</p>
                            <p className="overview-note">
                              {finding.workflow_name || 'Workflow context'} · Rank #{finding.priority_rank} · Score {finding.priority_score}
                            </p>
                          </div>
                          <span className="chip">{finding.severity}</span>
                        </a>
                      ))}
                    </div>
                  ) : (
                    <div className="empty-state-card empty-state-card-compact">
                      <strong>No active findings right now</strong>
                      <p>The latest completed analysis does not show any open issues for this system.</p>
                    </div>
                  )}
                </section>

                <section className="detail-card overview-card">
                  <div className="overview-section-header">
                    <strong>Impacted workflows</strong>
                    <a className="pill-link" href={`#/systems/${selectedSystem.id}/workflows`}>
                      Open workflows
                    </a>
                  </div>
                  {overview.impacted_workflows.length ? (
                    <div className="stack compact-stack">
                      {overview.impacted_workflows.map((workflow) => (
                        <a
                          className="overview-row-link"
                          href={workflow.workflow_id ? `#/systems/${selectedSystem.id}/workflows/${workflow.workflow_id}` : `#/systems/${selectedSystem.id}/workflows`}
                          key={workflow.workflow_id || workflow.name}
                        >
                          <div>
                            <strong>{workflow.name}</strong>
                            <p>{workflow.active_finding_count} active findings</p>
                          </div>
                          <span className="chip">{workflow.top_severity}</span>
                        </a>
                      ))}
                    </div>
                  ) : (
                    <div className="empty-state-card empty-state-card-compact">
                      <strong>No workflow hotspots yet</strong>
                      <p>Workflow impact will appear here once active findings are available.</p>
                    </div>
                  )}
                </section>

                <section className="detail-card overview-card">
                  <div className="overview-section-header">
                    <strong>Dominant components</strong>
                    <a className="pill-link" href={`#/systems/${selectedSystem.id}/components`}>
                      Open components
                    </a>
                  </div>
                  {overview.dominant_components.length ? (
                    <div className="stack compact-stack">
                      {overview.dominant_components.map((component) => (
                        <a className="overview-row-link" href={`#/systems/${selectedSystem.id}/components/${component.component_id}`} key={component.component_id}>
                          <div>
                            <strong>{component.name}</strong>
                            <p>{component.active_finding_count} active findings</p>
                          </div>
                          <span className="chip">{component.top_severity}</span>
                        </a>
                      ))}
                    </div>
                  ) : (
                    <div className="empty-state-card empty-state-card-compact">
                      <strong>No dominant component pattern yet</strong>
                      <p>Component hotspots appear when active findings localize to specific parts of the runtime graph.</p>
                    </div>
                  )}
                </section>
              </div>
            </section>

            <section className="overview-band">
              <SectionHeader title="Recommended actions" />
              {overview.recommended_next_actions.length ? (
                <div className="overview-actions-grid">
                  {overview.recommended_next_actions.map((action) => (
                    <a
                      className="overview-action-link"
                      href={
                        action.recommendation_id
                          ? `#/systems/${selectedSystem.id}/recommendations/${action.recommendation_id}`
                          : `#/systems/${selectedSystem.id}/findings/${action.finding_id}`
                      }
                      key={action.recommendation_id || action.finding_id}
                    >
                      <div className="overview-action-header">
                        <strong>{action.title}</strong>
                        <span className="chip">{action.action_type === 'recommendation' ? 'Recommended' : 'Investigate'}</span>
                      </div>
                      <p>{action.summary}</p>
                    </a>
                  ))}
                </div>
              ) : (
                <div className="empty-state-card">
                  <strong>No recommended actions yet</strong>
                  <p>Actions will appear here when active findings or linked recommendations are available.</p>
                </div>
              )}
            </section>

            <DisclosurePanel title="Supporting evidence" className="calm-overview-support">
              <div className="calm-details-grid">
                <EvidencePostureCard posture={overview.evidence_posture} title="Runtime freshness" maxScopes={4} />

                <section className="detail-card overview-card">
                  <strong>Severity mix</strong>
                  <div className="overview-chip-grid">
                    {['critical', 'high', 'medium', 'low'].map((severity) => (
                      <a className="overview-chip-link" href={findingsHref(selectedSystem.id, { severity })} key={severity}>
                        <span>{severity}</span>
                        <strong>{overview.severity_mix[severity] || 0}</strong>
                      </a>
                    ))}
                  </div>
                </section>

                <section className="detail-card overview-card">
                  <strong>Validation and confidence</strong>
                  <p>
                    {reliabilitySummary.workflows_requiring_validation_count}{' '}
                    stable {pluralize(reliabilitySummary.workflows_requiring_validation_count, 'workflow')} still{' '}
                    {pluralize(reliabilitySummary.workflows_requiring_validation_count, 'needs', 'need')} targeted validation coverage.
                  </p>
                  {reliabilitySummary.low_confidence_workflow_count ? (
                    <p className="overview-note">
                      {reliabilitySummary.low_confidence_workflow_count}{' '}
                      {pluralize(reliabilitySummary.low_confidence_workflow_count, 'workflow')} {pluralize(
                        reliabilitySummary.low_confidence_workflow_count,
                        'is',
                        'are'
                      )}{' '}
                      currently low confidence because recent evidence is sparse.
                    </p>
                  ) : (
                    <p className="overview-note">Validation pack preparation and validation result evidence are tracked separately.</p>
                  )}
                </section>
                {costClaimSummary ? (
                  <section className="detail-card overview-card overview-progress-card">
                    <div className="chapter-inline-header">
                      <strong>Cost and workflow units</strong>
                      <span className="chip chip-emphasis">{costClaimSummary.label}</span>
                    </div>
                    <p className="overview-progress-summary">{overviewSafeCostText(costClaimSummary.summary)}</p>
                    {primaryCostClaim ? <p>{overviewSafeCostText(primaryCostClaim.summary)}</p> : null}
                    <div className="labeled-stat-list">
                      <div className="labeled-stat-row">
                        <span>Observed claims</span>
                        <strong>{costClaimSummary.claim_count}</strong>
                      </div>
                      <div className="labeled-stat-row">
                        <span>Observed recoveries</span>
                        <strong>{costClaimSummary.recovered_claim_count}</strong>
                      </div>
                      {primaryCostValue ? (
                        <div className="labeled-stat-row">
                          <span>Primary unit</span>
                          <strong>{primaryCostValue}</strong>
                        </div>
                      ) : null}
                    </div>
                    {costClaimSummary.missing_inputs?.length ? (
                      <p className="overview-note">Next evidence needed: {costClaimSummary.missing_inputs.join(', ')}.</p>
                    ) : null}
                    <a className="pill-link" href={`#/systems/${selectedSystem.id}/compare`}>
                      Open compare
                    </a>
                  </section>
                ) : null}
              </div>
            </DisclosurePanel>

            <DisclosurePanel title="Runtime sources" className="calm-technical-details" summaryMeta={`${overview.runtime_sources?.length || 0} sources`}>
              <div className="chapter-header">
                <div />
                {canOperateRuntimeSources ? (
                  <a className="runtime-data-cta overview-runtime-action" href={`#/systems/${selectedSystem.id}/integrations`}>
                    <span className="runtime-data-cta-icon" aria-hidden="true">+</span>
                    <span>
                      <strong>Add runtime data</strong>
                      <small>Upload a new trace file</small>
                    </span>
                  </a>
                ) : null}
              </div>
              {runtimeSourceActionStatus ? <p className="overview-note">{runtimeSourceActionStatus}</p> : null}
              {overview.runtime_sources?.length ? (
                <div className="stack compact-stack">
                  {overview.runtime_sources.map((sourceConnection) => (
                    <article className="detail-card runtime-source-card" key={sourceConnection.id}>
                      <div className="workflow-mode-header">
                        <div>
                          <strong>{sourceConnection.display_name}</strong>
                          <p>
                            {sourceTypeLabel(sourceConnection.source_type)} · {sourceConnection.connection_mode}
                          </p>
                        </div>
                        <EvidencePostureChip posture={sourceConnection.evidence_posture} />
                      </div>

                      <TraceAnalysisSourceSummary analysis={sourceConnection.latest_ingestion_analysis} />

                      <div className="labeled-stat-list">
                        <div className="labeled-stat-row">
                          <span>Latest ingestion</span>
                          <strong>{sourceConnection.latest_ingestion_id || 'Not available yet'}</strong>
                        </div>
                        <div className="labeled-stat-row">
                          <span>Last success</span>
                          <strong>{formatEvidenceDateTime(sourceConnection.last_success_at)}</strong>
                        </div>
                        <div className="labeled-stat-row">
                          <span>Last failure</span>
                          <strong>{formatEvidenceDateTime(sourceConnection.last_failure_at)}</strong>
                        </div>
                        <div className="labeled-stat-row">
                          <span>Collection status</span>
                          <strong>{sourceConnection.last_collection_status || 'Unknown'}</strong>
                        </div>
                      </div>

                      {sourceConnection.last_error_message ? <p className="overview-note">{sourceConnection.last_error_message}</p> : null}
                      {canOperateRuntimeSources ? (
                        <div className="form-actions">
                          <button
                            className="secondary-button"
                            type="button"
                            disabled={runtimeSourceActionLoadingRef === `replay:${sourceConnection.id}` || !sourceConnection.latest_ingestion_id}
                            onClick={() => onReplayRuntimeSource(sourceConnection)}
                          >
                            {runtimeSourceActionLoadingRef === `replay:${sourceConnection.id}` ? 'Replaying…' : 'Replay latest'}
                          </button>
                          <button
                            className="secondary-button"
                            type="button"
                            disabled={runtimeSourceActionLoadingRef === `backfill:${sourceConnection.id}`}
                            onClick={() => onBackfillRuntimeSource(sourceConnection)}
                          >
                            {runtimeSourceActionLoadingRef === `backfill:${sourceConnection.id}`
                              ? 'Replaying window…'
                              : `Replay ${runtimeSourceWindowLabel(sourceConnection)}`}
                          </button>
                        </div>
                      ) : null}
                    </article>
                  ))}
                </div>
              ) : (
                <div className="empty-state-card">
                  <strong>No runtime sources configured yet</strong>
                  <p>Add a push source connection to start continuous runtime ingestion and freshness tracking.</p>
                </div>
              )}
            </DisclosurePanel>

            <section className="overview-band">
              <section className="detail-card overview-card overview-progress-card">
                <div className="chapter-inline-header">
                  <strong>Progress and evidence</strong>
                  <span className="chip chip-emphasis">{trendDirectionLabel(overview.posture.trend_direction)}</span>
                </div>
                <p className="overview-progress-summary">{overview.posture.trend_summary}</p>
                {overview.posture.low_data_reason ? <p className="overview-note">{overview.posture.low_data_reason}</p> : null}
                <div className="labeled-stat-list">
                  <div className="labeled-stat-row">
                    <span>Current window</span>
                    <strong>{analysisWindowSummary}</strong>
                  </div>
                  <div className="labeled-stat-row">
                    <span>Evidence range</span>
                    <strong>
                      {overview.analysis_window.started_at
                        ? `${formatDateTime(overview.analysis_window.started_at)} to ${formatDateTime(overview.analysis_window.ended_at)}`
                        : 'Waiting for completed analysis.'}
                    </strong>
                  </div>
                  <div className="labeled-stat-row">
                    <span>Validation coverage</span>
                    <strong>{validationGapSummary}</strong>
                  </div>
                  <div className="labeled-stat-row">
                    <span>Evidence confidence</span>
                    <strong>{lowConfidenceSummary}</strong>
                  </div>
                  <div className="labeled-stat-row">
                    <span>Snapshot refreshed</span>
                    <strong>{formatDateTime(overview.last_refreshed_at)}</strong>
                  </div>
                </div>
              </section>
            </section>
          </>
        )}
      </div>
    </section>
  );
}
